import { useEffect, useRef, useState } from "react";
import {
  faXmark,
  faClock,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  IOS_APP_STORE_URL,
  ANDROID_PLAY_STORE_URL,
} from "../utils/deviceRedirect";
import getTheAppQrCode from "../img/get-the-app-qr.png";
import getTheAppTestQrCode from "../img/get-the-app-qr-test.png";
import iosDownloadImage from "../img/IOS download image.svg";
import androidDownloadImage from "../img/android download image.png";

const DISMISSED_KEY = "mobileAppPromoDismissed";
const SNOOZE_UNTIL_KEY = "mobileAppPromoSnoozeUntil";

const appQrCode =
  process.env.REACT_APP_ENV === "production"
    ? getTheAppQrCode
    : getTheAppTestQrCode;

const MobileAppPromo = () => {
  const [visible, setVisible] = useState(false);
  const [showSnoozeOptions, setShowSnoozeOptions] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY) === "true";
    if (dismissed) return;

    const snoozeUntil = localStorage.getItem(SNOOZE_UNTIL_KEY);
    if (snoozeUntil && new Date() < new Date(snoozeUntil)) return;

    setVisible(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowSnoozeOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const snooze = (days) => {
    const until = new Date();
    until.setDate(until.getDate() + days);
    localStorage.setItem(SNOOZE_UNTIL_KEY, until.toISOString());
    setVisible(false);
  };

  const dismissForever = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 w-[90vw] max-w-sm animate-fade-in">
      <div className="relative rounded-2xl border border-[#e4d8ef] bg-white shadow-2xl p-5">
        <div
          className="absolute top-3 right-3 flex items-center gap-1"
          ref={menuRef}
        >
          <button
            onClick={() => setShowSnoozeOptions((prev) => !prev)}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Snooze options"
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </button>
          <button
            onClick={() => snooze(1)}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>

          {showSnoozeOptions && (
            <div className="absolute top-8 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm">
              <button
                onClick={() => snooze(7)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700"
              >
                <FontAwesomeIcon icon={faClock} className="text-gray-400" />
                Remind me in 7 days
              </button>
              <button
                onClick={() => snooze(30)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700"
              >
                <FontAwesomeIcon icon={faClock} className="text-gray-400" />
                Remind me in 30 days
              </button>
              <button
                onClick={dismissForever}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700"
              >
                Don't show again
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <img
            src={appQrCode}
            alt="Scan to get the School Wellbeing app"
            className="hidden sm:block h-24 w-24 shrink-0 object-contain rounded"
          />
          <div>
            <h3 className="text-sm font-bold text-[#3b214c] pr-6">
              Get the SWB mobile app
            </h3>
            <p className="mt-1 text-xs leading-4 text-[#656879]">
              Book services and manage your wellbeing on the go.
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a
            href={IOS_APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            className="flex h-9 w-28 shrink-0"
          >
            <img
              src={iosDownloadImage}
              alt="Download on the App Store"
              className="w-full h-full object-contain"
            />
          </a>
          <a
            href={ANDROID_PLAY_STORE_URL}
            target="_blank"
            rel="noreferrer"
            className="flex h-9 w-28 shrink-0"
          >
            <img
              src={androidDownloadImage}
              alt="Get it on Google Play"
              className="w-full h-full object-contain"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default MobileAppPromo;
